import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { task, type TaskInstance } from 'ember-concurrency';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import cloneDeep from 'lodash.clonedeep';
import type {
  Event,
  Holiday,
  Occupancy,
  Period,
  ShortTermRentalBreakdown,
  ShortTermRentalConfig,
  ShortTermRentalKpi,
  ShortTermRentalOccupancy,
  ShortTermRentalProperty,
  ShortTermRentalRate,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { DateAggregationType } from 'lumen/utils/constants/general-constants';
import {
  DEFAULT_NUMBER_OF_DAYS,
  NUMBER_OF_HISTORICAL_DAYS,
} from 'lumen/utils/constants/short-term-rental-constants';
import {
  BREAKDOWNS,
  CONFIG_DATA,
  EVENTS,
  HOLIDAYS,
  OCCUPANCY_OWN_HOTEL_KPIS,
  OCCUPANCY_PARAMS_RANGE,
  PROPERTIES_KPIS,
  RATES_KPIS,
  RATES_PARAMS_RANGE,
  RENTAL_KPIS,
  SHORT_TERM_RENTAL_PARAMS_RANGE,
} from 'lumen/utils/constants/short-term-rental-upsell-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { timeout } from 'lumen/utils/waiters';

export type Filters = {
  breakdown_filters: string[];
  price_range_end: number;
  price_range_start: number;
  radius: number;
};

type Model = {
  breakdowns: Partial<TaskInstance<ShortTermRentalBreakdown>>;
  config: ShortTermRentalConfig | null;
  defaultFilters: Insightpagesfavourite[] | null;
  endDate: string | null;
  evolutions: Partial<
    TaskInstance<{
      events: Event[];
      holidays?: Holiday[];
      occupancies?: Occupancy[];
      periods: Period[];
      shortTermRentalRates: ShortTermRentalRate[];
    }>
  >;
  kpis: Partial<TaskInstance<ShortTermRentalKpi[]>>;
  map: Partial<TaskInstance<ShortTermRentalProperty>>;
  startDate: string | null;
};

type Params = {
  activeFilters: string[];
  dateAggregation: DateAggregationType;
  fromDate: string | null;
  priceRangeEnd: number | null;
  priceRangeStart: number | null;
  radius: number | null;
  refDaysAgo: number;
  toDate: string | null;
};

export default class HotelShortTermRentalUpsellRoute extends Route {
  queryParams = {
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    radius: {
      refreshModel: true,
    },
    dateAggregation: {
      refreshModel: true,
    },
    refDaysAgo: {
      refreshModel: true,
    },
    activeFilters: {
      refreshModel: true,
      type: 'array',
    },
    priceRangeStart: {
      refreshModel: true,
      type: 'number',
    },
    priceRangeEnd: {
      refreshModel: true,
      type: 'number',
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare shortTermRentalUpsell: Services['short-term-rental-upsell'];
  @service declare store: Services['store'];

  lastDateAggregation: DateAggregationType | null = null;
  lastFilters: string[] = [];
  lastFromDate: string | null = null;
  lastRadius: number | null = null;
  lastRefDaysAgo: number | null = null;
  lastSubscriptionId: string | null = null;
  lastToDate: string | null = null;

  modelObject = trackedObject<Model>({
    breakdowns: { isRunning: true },
    config: null,
    defaultFilters: null,
    endDate: null,
    evolutions: { isRunning: true },
    kpis: { isRunning: true },
    map: { isRunning: true },
    startDate: null,
  });

  requestBreakdownsTask = task({ restartable: true }, async () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('short-term-rental-breakdown', BREAKDOWNS);

    await timeout();

    return this.store.peekAll<ShortTermRentalBreakdown>(
      'short-term-rental-breakdown',
    );
  });

  requestConfigDataTask = task({ restartable: true }, async () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('short-term-rental-config', CONFIG_DATA);

    await timeout();

    return this.store.peekRecord<ShortTermRentalConfig>(
      'short-term-rental-config',
      'test',
    );
  });

  requestDefaultFiltersTask = task(
    { restartable: true },
    async (hotelId: string) => {
      try {
        return await this.store.query<Insightpagesfavourite>(
          'insightpagesfavourite',
          {
            hotel: hotelId,
            namespace: 'api/v3',
            page_type: 'RENTALS',
            typeKey: 'insightpagesfavourites',
          },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  // eslint-disable-next-line @typescript-eslint/require-await
  requestEvolutionChartDataTask = task({ restartable: true }, async () => {
    const ratesParamsRange = cloneDeep(RATES_PARAMS_RANGE);

    ratesParamsRange.periods.forEach((period) => {
      // @ts-expect-error: Incorrect type
      period.rates[this.hotelAndMarketService.hotel.hotelinfo.id!] =
        period.rates['1'];
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload(ratesParamsRange);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload(
      'short-term-rental-rate',
      SHORT_TERM_RENTAL_PARAMS_RANGE,
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('events', EVENTS);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('holiday', HOLIDAYS);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('occupancy', OCCUPANCY_PARAMS_RANGE);

    const occupancyIds = new Set(
      OCCUPANCY_PARAMS_RANGE.occupancies.map(({ id }) => id),
    );

    const periodIds = new Set(ratesParamsRange.periods.map(({ id }) => id));

    const shortTermRentalRateIds = new Set(
      SHORT_TERM_RENTAL_PARAMS_RANGE.short_term_rental_rates.map(
        ({ id }) => id,
      ),
    );

    const shortTermRentalOccupancyIds = new Set(
      SHORT_TERM_RENTAL_PARAMS_RANGE.short_term_rental_occupancies.map(
        ({ id }) => id,
      ),
    );

    try {
      const events: Event[] = this.store.peekAll<Event>('event');

      const holidays: Holiday[] = this.store.peekAll<Holiday>('holiday');

      const occupancies: Occupancy[] | undefined = this.access.hasOTB
        ? this.store
            .peekAll<Occupancy>('occupancy')
            .filter(({ id }) => occupancyIds.has(id!))
        : undefined;

      const periods: Period[] = this.store
        .peekAll<Period>('period')
        .filter(({ id }) => periodIds.has(id!));

      const shortTermRentalRates: (
        | ShortTermRentalOccupancy
        | ShortTermRentalRate
      )[] = [
        ...this.store
          .peekAll<ShortTermRentalRate>('short-term-rental-rate')
          .filter(({ id }) => shortTermRentalRateIds.has(id!)),
        ...this.store
          .peekAll<ShortTermRentalOccupancy>('short-term-rental-occupancy')
          .filter(({ id }) => shortTermRentalOccupancyIds.has(id!)),
      ];

      return {
        events,
        holidays,
        occupancies,
        periods,
        shortTermRentalRates,
      };
    } catch (error) {
      this.errorTracking.captureException(error);
    }

    return null;
  });

  requestKpisTask = task({ restartable: true }, async () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('short-term-rental-kpi', RENTAL_KPIS);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('short-term-rental-kpi', PROPERTIES_KPIS);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('short-term-rental-kpi', RATES_KPIS);

    if (this.access.hasOTB) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload('short-term-rental-kpi', OCCUPANCY_OWN_HOTEL_KPIS);
    }

    const request = [
      [
        this.store.peekRecord<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          'occupancy.shorttermrental',
        ),
        this.store.peekRecord<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          'rate.shorttermrental',
        ),
      ],
      [
        this.store.peekRecord<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          'listings.relevant',
        ),
        this.store.peekRecord<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          'listings.total',
        ),
        this.store.peekRecord<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          'beds.relevant',
        ),
        this.store.peekRecord<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          'beds.total',
        ),
      ],
      [
        this.store.peekRecord<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          'rate.ownhotel',
        ),
      ],
    ];

    if (this.access.hasOTB) {
      request.push(
        this.store.peekAll<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          // @ts-expect-error: Expected 1 arguments, but got 2
          'occupancy.ownhotel',
        ),
      );
    }

    await timeout();
    return request;
  });

  requestMapDataTask = task(
    { restartable: true },
    async ({
      filters,
      subscription_id,
    }: {
      filters: string[];
      subscription_id: string;
    }) => {
      try {
        return await this.store.query<ShortTermRentalProperty>(
          'short-term-rental-property',
          {
            filters,
            namespace: 'api/v3',
            subscription_id,
            typeKey: 'shorttermrental/properties',
          },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otaId() {
    return this.hotelAndMarketService.hotel?.OTAsRates?.[0]?.id;
  }

  afterModel(): void {
    if (this.shortTermRentalUpsell.isShortTermRentalEndTrial) {
      void this.showEndTrialModal();
    }

    if (this.shortTermRentalUpsell.isShortTermRentalMidwayTrial) {
      void this.showMidwayTrialModal();
    }
  }

  beforeModel(): void {
    if (!this.access.hasFeature('short_term_rental.upsell')) {
      if (this.access.hasShortTermRentalAccess) {
        this.router.transitionTo('hotel.short-term-rental');
      }
    }
  }

  deactivate(): void {
    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }
    this.store.unloadAll('event');
    this.store.unloadAll('holiday');
    // unload for occupancy, day and period are commented because it was causing issues on route transition (specific going to Short-Term Rental page after starting the trial)
    // this.store.unloadAll('occupancy');
    // this.store.unloadAll('period');
    // this.store.unloadAll('day');
    // this.store.unloadAll('short-term-rental-rate');
    this.store.unloadAll('short-term-rental-breakdown');
    this.store.unloadAll('short-term-rental-config');
    this.store.unloadAll('short-term-rental-kpi');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async model(params: Params, transition: Transition) {
    const { dateAggregation, fromDate, refDaysAgo, toDate } = params;

    const rangeStart =
      fromDate ??
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM-DD');
    const rangeEnd =
      toDate ??
      this.hotelAndMarketService
        .getLocalTime()
        .add(DEFAULT_NUMBER_OF_DAYS, 'day')
        .format('YYYY-MM-DD');

    const today = this.hotelAndMarketService
      .getLocalTime()
      .format('YYYY-MM-DD');
    const offset = Math.max(dayjs(rangeStart).diff(dayjs(today), 'day'), 0);
    const daysSelected = dayjs(rangeEnd).diff(dayjs(rangeStart), 'day');

    const singleDaySelected = daysSelected === 0;

    // Subtract 1 day from the amount of days we want to go back in time, because today counts also as a day
    const startDate = singleDaySelected
      ? dayjs(rangeStart, 'YYYY-MM-DD')
          .subtract(NUMBER_OF_HISTORICAL_DAYS - 1 + offset, 'day')
          .format('YYYY-MM-DD')
      : rangeStart;

    this.modelObject.startDate = startDate;
    this.modelObject.endDate = rangeEnd;

    if (!this.modelObject.config) {
      this.modelObject.config = await this.requestConfigDataTask.perform();
    }

    this.modelObject.defaultFilters =
      await this.requestDefaultFiltersTask.perform(
        this.hotelAndMarketService.hotel.id!,
      );

    const {
      radius: defaultRadius,
      price_range_start: defaultPriceRangeStart,
      price_range_end: defaultPriceRangeEnd,
      breakdown_filters: defaultBreakdownFilters,
    } = this.modelObject.defaultFilters![0]!.filters as unknown as Filters;

    const { radiusLimit, priceRangeLimitMin, priceRangeLimitMax } =
      this.modelObject.config!;

    const radius = params.radius ?? defaultRadius ?? radiusLimit;
    const priceRangeStart =
      params.priceRangeStart ?? defaultPriceRangeStart ?? priceRangeLimitMin;
    const priceRangeEnd =
      params.priceRangeEnd ?? defaultPriceRangeEnd ?? priceRangeLimitMax;

    const activeFilters = params.activeFilters ?? defaultBreakdownFilters;

    const allFilters = [
      ...activeFilters,
      `radius.${radius}`,
      `price_range_start.${priceRangeStart}`,
      `price_range_end.${priceRangeEnd}`,
    ];

    const hasRouteChanged =
      transition.from &&
      !transition.from.name.startsWith('hotel.short-term-rental');

    const hasDateOrReferenceOrRouteOrFiltersChange =
      this.lastRefDaysAgo !== refDaysAgo ||
      this.lastFromDate !== fromDate ||
      this.lastToDate !== toDate ||
      hasRouteChanged ||
      JSON.stringify(this.lastFilters) !== JSON.stringify(allFilters);

    if (hasDateOrReferenceOrRouteOrFiltersChange) {
      this.lastRefDaysAgo = refDaysAgo;
      this.lastFromDate = fromDate;
      this.lastToDate = toDate;
      this.lastFilters = [...allFilters];
    }

    if (
      this.lastRadius !== radius ||
      hasDateOrReferenceOrRouteOrFiltersChange
    ) {
      // @ts-expect-error: Incorrect type
      this.modelObject.map = this.requestMapDataTask.perform({
        filters: allFilters,
        subscription_id: this.hotelAndMarketService.hotel.id!,
      });

      this.lastRadius = radius;
    }

    if (
      this.lastDateAggregation !== dateAggregation ||
      hasDateOrReferenceOrRouteOrFiltersChange
    ) {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await this.store.unloadAll('event');

      // @ts-expect-error: Incorrect type
      this.modelObject.evolutions =
        this.requestEvolutionChartDataTask.perform();

      this.lastDateAggregation = dateAggregation;
    }

    if (hasDateOrReferenceOrRouteOrFiltersChange) {
      // @ts-expect-error: Incorrect type
      this.modelObject.kpis = this.requestKpisTask.perform();

      // @ts-expect-error: Incorrect type
      this.modelObject.breakdowns = this.requestBreakdownsTask.perform();
    }

    return this.modelObject;
  }

  private async showEndTrialModal(): Promise<void> {
    const confirmWasClicked = await this.alerting.showErrorAlert({
      title: this.intl.t('short_term_rental.trial.end.title'),
      text: this.intl.t('short_term_rental.trial.message'),
      buttons: {
        cancel: {
          text: this.intl.t('upsell.no_thanks'),
        },
        confirm: {
          text: this.intl.t('general.contact_me'),
        },
      },
      unsafe_outsideClickCanCloseAlert: false,
    });

    if (confirmWasClicked) {
      // @ts-expect-error: requestGetInTouchTrial is undefined
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.shortTermRentalUpsell.requestGetInTouchTrial.perform();
    } else {
      // @ts-expect-error: requestNotInterestedTrial is undefined
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.shortTermRentalUpsell.requestNotInterestedTrial.perform();
    }
  }

  private async showMidwayTrialModal(): Promise<void> {
    const confirmWasClicked = await this.alerting.showWarningAlert({
      title: this.intl.t('short_term_rental.trial.midway.title', {
        days: this.shortTermRentalUpsell.daysLeftInTrial,
      }),
      text: this.intl.t('short_term_rental.trial.message'),
      buttons: {
        cancel: {
          text: this.intl.t('general.not_now'),
        },
        confirm: {
          text: this.intl.t('general.contact_me'),
        },
      },
      unsafe_outsideClickCanCloseAlert: false,
    });

    if (confirmWasClicked) {
      void this.shortTermRentalUpsell.requestGetInTouchTrialTask.perform();
    } else {
      void this.shortTermRentalUpsell.requestNotNowTrialTask.perform();
    }
  }
}
